import { MAX_GLOBAL_SEARCH_OPTIONS } from "@/constants";
import { Icon, MenuItem } from "@trimble/modus-cmx";
import { useTranslation } from "react-i18next";
import { highlightMatch } from "@/components/app/Navigation/GlobalHeader/helper";
import type {
  CustomerCompanyOption,
  CustomerOptionProps,
  CustomerRoleOption,
} from "@/components/Navigation/GlobalHeader/GlobalSearch/types";
import { ViewAllOption } from "../ViewAllOption/ViewAllOption";

export function CustomerOption({
  companyOptions,
  roleOptions,
  totalCount,
  searchTerm,
  onViewAll,
}: CustomerOptionProps) {
  const { t } = useTranslation();

  const companyRows = companyOptions.slice(0, MAX_GLOBAL_SEARCH_OPTIONS);
  const rolesRows = roleOptions.slice(0, MAX_GLOBAL_SEARCH_OPTIONS);

  const renderCustomerRow = (
    option: CustomerCompanyOption,
    rowIndex: number
  ) => (
    <MenuItem
      key={`company-${option.customerId}-${rowIndex}`}
      value={option.customerId}
    >
      <div slot="start-icon" className="option option--dealer">
        <Icon
          name="building_corporate"
          variant="solid"
          size="xs"
          decorative
          className="icon-color"
        />
        <div className="option-details global-search-customer-contact-lines">
          <span className="global-search-customer-company-name">
            {highlightMatch(option.companyName, searchTerm)}
          </span>
          {option.ownerEmail ? (
            <div className="global-search-customer-email-row">
              <span className="global-search-customer-email-adjacent">
                <span className="option-customer-name global-search-customer-email-text">
                  {highlightMatch(option.ownerEmail, searchTerm)}
                </span>
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </MenuItem>
  );

  const renderRolesRow = (option: CustomerRoleOption, rowIndex: number) => (
    <MenuItem
      key={`roles-${option.customerId}-${rowIndex}`}
      value={option.customerId}
    >
      <div slot="start-icon" className="option option--dealer">
        <Icon
          name="person"
          variant="solid"
          size="xs"
          decorative
          className="icon-color"
        />
        <div className="option-details global-search-customer-contact-lines">
          <span className="global-search-customer-company-name">
            {highlightMatch(
              `${option.customer.firstName} ${option.customer.lastName}`,
              searchTerm
            )}
          </span>
          <div className="global-search-customer-email-row">
            <span className="global-search-customer-email-adjacent">
              <span className="option-customer-name global-search-customer-email-text">
                {highlightMatch(option.customer.email, searchTerm)}
              </span>
            </span>
          </div>
        </div>
      </div>
    </MenuItem>
  );

  return (
    <>
      {companyRows.length > 0 ? (
        <>
          <div
            className="global-search-customer-section-heading"
            role="presentation"
          >
            {t("global_search.customers_section_company")}
          </div>
          <div className="global-search-customer-section-rows">
            {companyRows.map((option, index) =>
              renderCustomerRow(option, index)
            )}
          </div>
        </>
      ) : null}

      {rolesRows.length > 0 ? (
        <>
          <div
            className="global-search-customer-section-heading"
            role="presentation"
          >
            {t("global_search.customers_section_role")}
          </div>
          <div className="global-search-customer-section-rows">
            {rolesRows.map((option, index) => renderRolesRow(option, index))}
          </div>
        </>
      ) : null}

      {totalCount > MAX_GLOBAL_SEARCH_OPTIONS ? (
        <ViewAllOption
          totalCount={totalCount}
          onViewAll={onViewAll}
          searchCategory="customers"
        />
      ) : null}
    </>
  );
}
